import { VENDORED_INTEGRATION_LOGOS } from "@frontend-shared/constants";
import type { TIntegrationIconComponent } from "@frontend-shared/types";
import { memo } from "react";

import { IntegrationPlaceholder } from "./IntegrationPlaceholder";

interface ICreateVendoredLogoProps {
  source: string;
}

/**
 * NOTE: Wraps a mirrored brand logo in the same component shape the hand-drawn
 * integration SVGs expose, so a caller renders `<Icon className="…" />` without
 * caring which of the two it got.
 *
 * `alt=""` by default, because most call sites label the integration next to
 * the tile and a repeated brand name is noise to a screen reader. A caller that
 * names the logo — `IntegrationIcon` with an `ariaLabel`, as the admin
 * integration pages do — gets that name as the `alt`, the same accessible name
 * an authored SVG exposes through `role="img"` + `aria-label`.
 */
const createVendoredLogo = ({
  source,
}: ICreateVendoredLogoProps): TIntegrationIconComponent =>
  memo(({ className, "aria-label": ariaLabel, "aria-hidden": ariaHidden }) => (
    <img
      src={source}
      alt={ariaLabel ?? ""}
      aria-hidden={ariaHidden}
      className={className}
      loading="lazy"
    />
  ));

const VENDORED_LOGO_COMPONENTS: Partial<
  Record<string, TIntegrationIconComponent>
> = Object.fromEntries(
  Object.entries(VENDORED_INTEGRATION_LOGOS).map(([providerId, source]) => [
    providerId,
    createVendoredLogo({ source }),
  ]),
);

interface IResolveIntegrationIconProps {
  providerId: string;
  authored?: TIntegrationIconComponent | undefined;
}

/**
 * NOTE: Resolution order is mirrored Pipedream logo → authored SVG →
 * placeholder. The mirror wins.
 *
 * It used to be the other way round, on the reasoning that the authored icons
 * were "vector, theme-aware and hand-checked against the brand". Rendering all
 * 48 of them on a contact sheet showed that is not what they are: every one is
 * a single hue with a per-path lightness ladder
 * (`hsl(from hsl(<one hue>) h s 37%)`, `… 50%`, `… 76%`), which is a monochrome
 * tint mechanism. A multi-colour mark cannot be expressed in it at all, so
 * Google Drive rendered as one blue, Google Meet as one green, Gmail as one red
 * and Slack as one magenta — the review's "the Google icons are not like the
 * original ones".
 *
 * Preferring those over Pipedream's official artwork meant the 48 brands people
 * actually recognise were the 48 that would never get a real logo, however many
 * times the mirror was refreshed. The authored set stays as the fallback: it
 * still covers the NSAI-native providers Pipedream has no app for (`mcps`,
 * `pdf-generator`, `kontur-*`), and it covers everything until the mirror is
 * first populated.
 */
export const resolveIntegrationIcon = ({
  providerId,
  authored,
}: IResolveIntegrationIconProps): TIntegrationIconComponent =>
  VENDORED_LOGO_COMPONENTS[providerId] ?? authored ?? IntegrationPlaceholder;
